import React, { useState } from 'react';
import { useTranslation } from "react-i18next";
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import { Button, Chip, LinearProgress, Skeleton, ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material";
import Box from '@mui/material/Box';
import PieChartOutlineIcon from '@mui/icons-material/PieChartOutline';
import BarChartIcon from '@mui/icons-material/BarChart';
import ChartBanner from '../charts/ChartBanner';
import { formatValue, normalize, percentageFormat } from '../../../../helpers/helpers';
import { ChartType } from '../../../../models/dashboard';
import ComparePieChart from './ComparedPieChart';
import { TableChip } from '../../../../components/EnhancedTableToolbar';

export interface ComparedPieBarChartData {
    label: string;
    value: number;
    comparedValue: number;
}

export interface ComparedPieBarChartProps {
    title: string;
    description: string;
    data: ComparedPieBarChartData[];
    currency?: string;
    pieLabels?: string[];
    locale?: string;
    isLoading?: boolean;
    error?: boolean;
    reportName?: string;
    reportLink?: string;
    height?: number;
    chips?: TableChip[];
}

const scrollCSS = {
    "&::-webkit-scrollbar": {
        width: "5px",
    },
    "&::-webkit-scrollbar-track": {
        background: "#f1f1f1",
        borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb": {
        background: "#888",
        borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
        background: "#555",
    },
}

const ComparedPieBarChart = (props: ComparedPieBarChartProps) => {
    const {
        title,
        description,
        data,
        currency,
        pieLabels,
        locale,
        isLoading,
        error,
        reportName,
        reportLink,
        height,
        chips,
    } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const [chartType, setChartType] = useState<ChartType>('bar');
    const valueTotal = data?.reduce(((x, y) => x + y.value), 0) || 1;
    const comparedTotal = data?.reduce(((x, y) => x + y.comparedValue), 0) || 1;
    data?.sort((a, b) => b.value - a.value)

    const toggleChart = (
        event: React.MouseEvent<HTMLElement>,
        newChartType: ChartType,
    ) => {
        if (newChartType !== null) {
            setChartType(newChartType);
        }
    };

    return (
        <Card elevation={0} sx={{ height, border: `solid 1px`, borderColor: theme.palette.grey['12p'], marginX: '8px', padding: '12px 16px', flex: 1, display: 'flex', flexDirection: 'column', breakInside: 'avoid', pageBreakInside: 'avoid' }}>
            <Box sx={{ marginRight: '4px', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {error && <ChartBanner type='error' title={t("screens.dashboarding.dataError")} />}
                {!error && !data?.length && <ChartBanner type='warning' title={t("screens.dashboarding.noDataAvailable")} />}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto', justifyContent: 'space-between' }}>
                {isLoading && <Skeleton variant='rectangular' sx={{ width: '70px', height: '60px' }} />}
                {!isLoading &&
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Box>
                            <Typography variant="h2">{title}</Typography>
                            <Typography sx={{ fontSize: '14px', fontWeight: '400' }}>{description}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, marginTop: 1 }}>
                            {chartType !== 'pie' && chips?.map((chip) => (
                                <Chip
                                    key={`chip-${chip.label}`}
                                    variant="filled"
                                    size='small'
                                    label={chip.label}
                                    sx={{
                                        marginRight: '8px',
                                        backgroundColor:
                                            chip.labelType === 'periodCompare'
                                                ? theme?.palette?.warning['190p']
                                                : theme.palette.info['190p'],
                                        '& .MuiChip-label': {
                                            color: chip.labelType === 'periodCompare' ? theme.palette.warning['160p'] : theme.palette.info['160p']
                                        }
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                }
                <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto' }}>
                    {isLoading && <Skeleton variant='rectangular' sx={{ width: '110px', height: '60px' }} />}
                    {!isLoading && data?.length > 0 &&
                        <ToggleButtonGroup color='primary' value={chartType} exclusive onChange={toggleChart}>
                            <ToggleButton value='bar' sx={{ border: 'none', borderRadius: '50% !important', margin: '5px' }}>
                                <BarChartIcon />
                            </ToggleButton>
                            <ToggleButton value='pie' sx={{ border: 'none', borderRadius: '50% !important', margin: '5px' }}>
                                <PieChartOutlineIcon />
                            </ToggleButton>
                        </ToggleButtonGroup>
                    }
                </Box>
            </Box>
            {!error && !data?.length && <Box sx={{ flexGrow: 1 }} />}
            {chartType === 'bar' &&
                <Box sx={{ overflow: 'auto', marginTop: '20px', ...scrollCSS }}>
                    {isLoading && <Skeleton variant='rectangular' sx={{ height: '320px' }} />}
                    {!isLoading &&
                        <Box>
                            {data?.map(item => (
                                <Box key={`${item.label}-${item.value}`} sx={{ marginRight: '10px' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto', marginTop: '10px', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontSize: '16px', fontWeight: '600' }}>{item.label as string}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', MarginBottom: '24px' }}>
                                        <Box sx={{ width: '70%', marginRight: '8px' }}>
                                            <LinearProgress variant="determinate" value={normalize(item.value / valueTotal)}
                                                sx={{
                                                    height: 7,
                                                    bgcolor: theme.palette.info['190p'],
                                                    borderRadius: 5,
                                                    "& .MuiLinearProgress-bar": { borderRadius: 5 }
                                                }}
                                            />
                                        </Box>
                                        <Typography sx={{ fontSize: '16px', fontWeight: '400', marginRight: '8px' }}>{formatValue(item.value, !!currency, currency, locale)}</Typography>
                                        <Typography sx={{ fontSize: '16px', fontWeight: '400' }}>{percentageFormat(item.value / valueTotal, locale)}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', MarginBottom: '24px' }}>
                                        <Box sx={{ width: '70%', marginRight: '8px' }}>
                                            <LinearProgress variant="determinate" value={normalize(item.comparedValue / comparedTotal)}
                                                sx={{
                                                    height: 7,
                                                    bgcolor: theme?.palette?.warning['190p'],
                                                    borderRadius: 5,
                                                    "& .MuiLinearProgress-bar": { backgroundColor: theme.palette.amber.main, borderRadius: 5 }
                                                }}
                                            />
                                        </Box>
                                        <Typography sx={{ fontSize: '16px', fontWeight: '400', marginRight: '8px' }}>{formatValue(item.comparedValue, !!currency, currency, locale)}</Typography>
                                        <Typography sx={{ fontSize: '16px', fontWeight: '400' }}>{percentageFormat(item.comparedValue / comparedTotal, locale)}</Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    }
                </Box>
            }
            {chartType === 'pie' && <ComparePieChart data={data} pieLabels={pieLabels} height={height ? height - (reportName && reportLink ? 114 : 56) : undefined} currency={currency} />}
            {reportName && reportLink &&
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'auto' }}>
                    <Button href={reportLink} sx={{ fontSize: '16px', fontWeight: '600', color: 'primary.main' }}>{reportName}</Button>
                </Box>
            }
        </Card>
    );
}

export default ComparedPieBarChart;
